import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    StyledBottomNavigation,
    StyledBottomNavigationAction,
} from '../styles/navigationStyles';
import { ReactComponent as More_open } from '../../../assets/images/menu_icons/more_open.svg';
import { ReactComponent as More_closed } from '../../../assets/images/menu_icons/more_closed.svg';
import { NavigationIcon } from './NavigationItem';
import MoreDrawer from './MoreDrawer';
import { NAVIGATION_DIMENSIONS } from '../constants/navigationTheme';
import { ARIA_LABELS } from '../constants/navigationConfig';
import { useLocation } from 'react-router-dom';

const { MOBILE_VISIBLE_ITEMS } = NAVIGATION_DIMENSIONS;

/**
 * Componente de navegação mobile (bottom navigation)
 */
const MobileNav = ({ navigationItems, onNavigate }) => {
    const [openMoreDrawer, setOpenMoreDrawer] = useState(false);
    const location = useLocation();

    useEffect(() => {
        if (openMoreDrawer) {
            setOpenMoreDrawer(false);
        }
    }, [location]);

    // Separa itens visíveis e ocultos
    const mobileVisibleItems = useMemo(
        () => navigationItems.slice(0, MOBILE_VISIBLE_ITEMS),
        [navigationItems]
    );

    const mobileHiddenItems = useMemo(
        () => navigationItems.slice(MOBILE_VISIBLE_ITEMS),
        [navigationItems]
    );

    // Handlers
    const handleToggleMoreDrawer = useCallback(() => {
        setOpenMoreDrawer((prev) => !prev);
    }, []);

    const handleCloseMoreDrawer = useCallback(() => {
        setOpenMoreDrawer(false);
    }, []);

    const handleMainNavigation = useCallback(
        (index) => {
            if (index < MOBILE_VISIBLE_ITEMS) {
                onNavigate(navigationItems[index], handleCloseMoreDrawer);
            }
        },
        [navigationItems, onNavigate, handleCloseMoreDrawer]
    );

    const handleMoreItemClick = useCallback(
        (item) => {
            handleCloseMoreDrawer();
            setTimeout(() => {
                setTimeout(() => {
                    setTimeout(() => {
                        onNavigate(item);
                    }, 100);
                }, 100);
            }, 100);
        },
        [onNavigate, handleCloseMoreDrawer]
    );

    return (
        <>
            <StyledBottomNavigation
                value={false}
                onChange={(event, newValue) => handleMainNavigation(newValue)}
                showLabels
                isDrawerOpen={openMoreDrawer}
                component="nav"
                aria-label={ARIA_LABELS.MAIN_NAVIGATION}
            >
                {mobileVisibleItems.map((item) => (
                    <StyledBottomNavigationAction
                        key={item.id}
                        label={item.label}
                        icon={<NavigationIcon item={item} />}
                        className={item.className}
                        aria-label={item.label}
                        data-test={`mainMenuMobile_${item.className}_btn`}
                    />
                ))}
                <StyledBottomNavigationAction
                    label="Mais"
                    icon={openMoreDrawer ? <More_open aria-hidden="true" /> : <More_closed aria-hidden="true" />}
                    onClick={handleToggleMoreDrawer}
                    className="more"
                    aria-label={ARIA_LABELS.OPEN_MORE_MENU}
                    aria-expanded={openMoreDrawer}
                    data-test="mainMenuMobile_more_btn"
                />
            </StyledBottomNavigation>

            <MoreDrawer
                open={openMoreDrawer}
                onClose={handleCloseMoreDrawer}
                items={mobileHiddenItems}
                onItemClick={handleMoreItemClick}
            />
        </>
    );
};

MobileNav.propTypes = {
    navigationItems: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.elementType.isRequired,
            route: PropTypes.string.isRequired,
            className: PropTypes.string,
            showBadge: PropTypes.bool,
        })
    ).isRequired,
    onNavigate: PropTypes.func.isRequired,
};

export default MobileNav;